/**
 * Distribute a flat list of `paragraph_words` (DB rows tied to a transcription,
 * not to individual paragraphs) into the paragraph that owns each word's time.
 *
 * The backend stores `paragraph_words` per-transcription (only `transcription_id`
 * — no `paragraph_id` column). We have to figure out which words belong to which
 * paragraph from the client side. The right way to do this is by time range:
 * every paragraph has a start time and either an explicit duration or it ends
 * when the next paragraph starts; any DB word whose `offsetSeconds` falls in
 * that half-open interval `[start, end)` belongs to that paragraph.
 *
 * The previous implementation in Session.jsx sliced the global word list by
 * rendered-text word *count* (orphan distribution), which compounded misalign-
 * ment across the session whenever rendered count diverged from DB count for
 * any paragraph. This function replaces that with strict time-range bucketing.
 *
 * Word `offsetSeconds` is treated as authoritative — we do NOT rewrite offsets
 * to make the first word align with `paragraph.start`. If the speaker paused
 * at the start of a paragraph, the first DB word will have an offset slightly
 * later than `paragraph.start` and the highlight will fire at the actual
 * spoken time. That is the correct behavior.
 *
 * IMPORTANT: the `end` / `duration_seconds` field on paragraphs is treated as
 * informational only — NOT used to bound where a paragraph stops. Real-world
 * data (session 49 in our system) has paragraphs whose `end` is the duration
 * of only the first utterance (e.g. 2.52s for a 4-minute welcome speech),
 * which would chop the paragraph to a few words and drop everything after into
 * "no paragraph" land. We deliberately use the NEXT paragraph's start (or
 * +Infinity for the last paragraph) as the end boundary instead. If the
 * speaker actually stopped early and there's silence before the next
 * paragraph, there are simply no DB words in that gap and nothing is
 * misassigned.
 *
 * @param {Object} input
 * @param {Array<{id, start, ...}>} input.paragraphs
 *   `start` in seconds (already parsed from ISO). Any other fields are
 *   passed through untouched. The end boundary used for bucketing is the
 *   next paragraph's `start` (or +Infinity for the last paragraph) — the
 *   `end` / `duration_seconds` fields are NOT consulted.
 * @param {Array<{offsetSeconds, ...}>} input.paragraphWords
 *   Each must have a finite `offsetSeconds`. Order is not required — this
 *   function sorts internally.
 * @returns {Array<paragraph & {paragraph_words: word[]}>}
 *   Same paragraphs (preserving original input order), each with a
 *   `paragraph_words` array containing the words whose `offsetSeconds`
 *   falls into the paragraph's time range.
 */
export function distributeWordsToParagraphs({ paragraphs, paragraphWords }) {
  if (!Array.isArray(paragraphs) || paragraphs.length === 0) return [];
  const words = Array.isArray(paragraphWords) ? paragraphWords : [];

  // Sort paragraphs by start time, but remember original positions so we can
  // return the result in input order.
  const indexed = paragraphs.map((p, originalIndex) => ({ p, originalIndex }));
  const byStart = [...indexed].sort(
    (a, b) => (Number(a.p.start) || 0) - (Number(b.p.start) || 0)
  );

  // Sort words by offsetSeconds. Drop any without a finite offset — they
  // can't be placed and would only contaminate later assignments.
  const sortedWords = words
    .filter((w) => Number.isFinite(w?.offsetSeconds))
    .sort((a, b) => a.offsetSeconds - b.offsetSeconds);

  // Walk paragraphs in time order, walking a pointer through the sorted
  // word list. Each word is placed into the first paragraph whose range
  // contains its offsetSeconds, or dropped if no paragraph covers it.
  const bucketByIndex = new Map();
  let wordPtr = 0;

  for (let i = 0; i < byStart.length; i += 1) {
    const { p, originalIndex } = byStart[i];
    const startSec = Number(p.start) || 0;
    // End boundary is purely "where does the next paragraph start". The
    // `duration_seconds` / `end` field is ignored on purpose — see the
    // module docstring (session 49 has a 2.52s `end` on a 4-minute speech).
    const nextStartSec =
      i + 1 < byStart.length ? Number(byStart[i + 1].p.start) : null;
    const endBoundary = Number.isFinite(nextStartSec) ? nextStartSec : Infinity;

    // Skip words that fall before this paragraph's start (no paragraph
    // owned them — likely metadata silence before the transcript began).
    while (
      wordPtr < sortedWords.length &&
      sortedWords[wordPtr].offsetSeconds < startSec
    ) {
      wordPtr += 1;
    }

    const bucket = [];
    while (
      wordPtr < sortedWords.length &&
      sortedWords[wordPtr].offsetSeconds < endBoundary
    ) {
      bucket.push(sortedWords[wordPtr]);
      wordPtr += 1;
    }
    bucketByIndex.set(originalIndex, bucket);
  }

  return paragraphs.map((p, idx) => ({
    ...p,
    paragraph_words: bucketByIndex.get(idx) || [],
  }));
}
